import { DatePipe, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  inject,
  model,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormBuilder,
  FormControl,
  FormGroupDirective,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { groupBy, isEqual } from 'lodash';
import { firstValueFrom } from 'rxjs';

import { MultipleVendorSelectComponent } from '@features/multiple-vendor-select/multiple-vendor-select.components';
import { PatientCurvesDropdownComponent } from '@features/patient-curves-dropdown/patient-curves-dropdown.components';

import { TimelineQuery } from '@models/milestone-category/milestone/timeline.query';
import { injectVendorsAmountTypes } from '@models/organization/api/vendors.api';
import { PatientCurveQuery } from '@models/patient-curve/patient-curve.query';
import { SiteCurveService } from '@models/site-curve/site-curve.service';

import { CustomValidators } from '@shared/components/base-form-control/custom-validators';
import { CheckboxControlComponent } from '@shared/components/checkbox/checkbox-control.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import { DatePickerComponent } from '@shared/components/input/date-picker/date-picker.component';
import { InputComponent } from '@shared/components/input/input.component';
import { ModalComponent } from '@shared/components/modals/modal/modal.component';
import { CustomOverlayRef } from '@shared/components/overlay/custom-overlay-ref';
import { RadioButtonGroupComponent } from '@shared/components/radio-button/radio-button-group.component';
import { RadioButtonComponent } from '@shared/components/radio-button/radio-button.component';
import { FormControlConstants } from '@shared/constants/form-control.constants';
import { FormErrorDirective } from '@shared/directives/form-error.directive';
import { SkeletonLoaderDirective } from '@shared/directives/skeleton-loader.directive';
import { TooltipDirective } from '@shared/directives/tooltip.directive';
import {
  AmountType,
  CurveType,
  ForecastMethodType,
  listDriverCustomGroupsWithDataQuery,
  listDriverSiteGroupsQuery,
} from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';
import { Option } from '@shared/types/components.type';
import { markAllFormFieldsAsTouched, scrollToFirstError } from '@shared/utils/form.utils';

import { QuickForecastCostCategoryInfoBlockComponent } from './components/quick-forecast-cost-category-info-block/quick-forecast-cost-category-info-block.component';
import { ForecastSettings, QuickForecastService } from './services/quick-forecast.service';
import { QuickForecastValidators } from './services/quick-forecast.validators';

interface MilestoneItem {
  categoryId: string;
  categoryName: string;
  contractEndDate: string;
  contractStartDate: string;
  id: string;
  milestoneName: string;
  name: string;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    CheckboxControlComponent,
    RadioButtonGroupComponent,
    RadioButtonComponent,
    NgSelectModule,
    NgTemplateOutlet,
    PatientCurvesDropdownComponent,
    FormErrorDirective,
    DatePipe,
    IconComponent,
    DatePickerComponent,
    ModalComponent,
    TooltipDirective,
    MultipleVendorSelectComponent,
    QuickForecastCostCategoryInfoBlockComponent,
    SkeletonLoaderDirective,
  ],
  providers: [QuickForecastService],
  styleUrl: './quick-forecast-modal.component.scss',
  templateUrl: './quick-forecast-modal.component.html',
})
export class QuickForecastModalComponent {
  readonly patientCurveQuery = inject(PatientCurveQuery);

  private readonly cdr = inject(ChangeDetectorRef);
  private readonly fb = inject(FormBuilder);
  private readonly overlayService = inject(OverlayService);
  private readonly quickForecastService = inject(QuickForecastService);
  private readonly siteCurveService = inject(SiteCurveService);
  private readonly timelineQuery = inject(TimelineQuery);

  vendorsAmountTypesQuery = injectVendorsAmountTypes();

  readonly allSiteCurves = signal<listDriverSiteGroupsQuery[]>([]);
  readonly siteCurves = signal<listDriverSiteGroupsQuery[]>([]);
  readonly submitting = signal(false);

  mapVendorAmountTypes = computed(() => {
    const vendorAmountTypes = this.vendorsAmountTypesQuery.data() || [];

    return vendorAmountTypes.reduce(
      (map, item) => map.set(item.organization_id, item.amount_types),
      new Map<string, AmountType[]>()
    );
  });
  readonly milestonePhases = computed(() => {
    if (!this.timelineQuery.timeline().length) {
      return [];
    }

    const phases = groupBy(this.timelineQuery.timeline(), 'milestone.milestone_category.name');

    return Object.keys(phases).map((key) => {
      const firstPhase = phases[key][0];
      const lastPhase = phases[key][phases[key].length - 1];

      return {
        categoryName: key,
        contractEndDate: lastPhase.contract_end_date,
        contractEndDateId: lastPhase.milestone.id,
        contractStartDate: firstPhase.contract_start_date,
        contractStartDateId: firstPhase.milestone.id,
        id: firstPhase.milestone.milestone_category.id,
      };
    });
  });
  readonly milestones = computed(() => {
    return this.timelineQuery.timeline().reduce<MilestoneItem[]>((accum, item) => {
      accum.push({
        categoryId: item.milestone.milestone_category.id,
        categoryName: item.milestone.milestone_category.name,
        contractEndDate: item.contract_end_date,
        contractStartDate: item.contract_start_date,
        id: item.milestone.id,
        milestoneName: item.milestone.name,
        name: item.milestone.name,
      });

      return accum;
    }, []);
  });

  private readonly ref = inject<
    CustomOverlayRef<
      unknown,
      {
        categoryTypes: string[];
        customCurves: listDriverCustomGroupsWithDataQuery[];
        vendorId: string;
      }
    >
  >(CustomOverlayRef);

  selectedVendorIDs = model<string[]>(this.ref?.data?.vendorId ? [this.ref?.data?.vendorId] : []);

  submittingButtonDisabled = computed(() => this.submitting() || !this.selectedVendorIDs().length);
  submittingButtonTooltip = computed(() =>
    !this.selectedVendorIDs().length ? 'You must select at least one Vendor Contract' : ''
  );
  readonly trialTimelinePeriod = computed(() => {
    const milestones = this.milestones();

    return {
      endDate: milestones[milestones.length - 1].contractEndDate,
      startDate: milestones[0].contractStartDate,
    };
  });

  readonly formGroup = viewChild<FormGroupDirective>('ngForm');

  amountTypes = AmountType;
  readonly customCurves = (this.ref.data?.customCurves || []).map((curve) => ({
    name: curve.name,
    value: curve.driver_setting_id,
  }));
  readonly form = this.fb.group({
    applyInvestigator: false,
    applyPassThrough: false,
    applyServices: false,
    // if custom driver selected
    customCurve: [null, QuickForecastValidators.required],
    customEndMilestone: new FormControl(null, {
      updateOn: 'blur',
      validators: [
        QuickForecastValidators.isDateInInclusiveRange(
          this.trialTimelinePeriod().startDate,
          this.trialTimelinePeriod().endDate
        ),
      ],
    }),
    customStartMilestone: new FormControl(null, {
      updateOn: 'blur',
      validators: [
        QuickForecastValidators.isDateInInclusiveRange(
          this.trialTimelinePeriod().startDate,
          this.trialTimelinePeriod().endDate
        ),
      ],
    }),
    driver: ['', Validators.required],
    endMilestone: null,
    // if time driver selected
    milestone: null as null | string,
    // if patient driver selected
    patientCurve: [null, QuickForecastValidators.required],
    // general time validation to render error section
    requiredTimeValidation: null,
    siteCurve: [null, QuickForecastValidators.required],
    // if site driver selected
    siteDriverPeriod: [null, QuickForecastValidators.required],
    startMilestone: null,
    timeMethod: [null as ForecastMethodType | null],
  });
  readonly methodTimeOptions: Option<ForecastMethodType>[] = [
    {
      label: 'Straight Line',
      value: ForecastMethodType.FORECAST_STRAIGHTLINE,
    },
    {
      label: 'Frontloaded',
      value: ForecastMethodType.FORECAST_FRONTLOADED,
    },
    {
      label: 'Backloaded',
      value: ForecastMethodType.FORECAST_BACKLOADED,
    },
  ];
  readonly oneOfValidationMessage = FormControlConstants.VALIDATION_MESSAGE.ONE_OF;
  readonly periodSiteOptions: Option<CurveType>[] = [
    {
      label: 'Net Sites',
      value: CurveType.NET,
    },
    {
      label: 'Sites Activated',
      value: CurveType.ACTIVATION,
    },
    {
      label: 'Sites Closed',
      value: CurveType.CLOSE_OUT,
    },
  ];

  private readonly defaultValues = this.form.getRawValue();
  private readonly mapValidationForBeforeSubmit = new Map<string, () => boolean>([
    ['custom', () => QuickForecastValidators.isCustomDriverFormValid(this.form)],
    ['patient', () => QuickForecastValidators.isPatientDriverFormValid(this.form)],
    ['site', () => QuickForecastValidators.isSiteDriverFormValid(this.form)],
    [
      'time',
      () =>
        QuickForecastValidators.isTimeDriverFormValid(
          this.form,
          this.isAnyTimelineMilestoneHasValue
        ),
    ],
  ]);

  closeModal = async () => {
    if (this.submitting()) {
      return;
    }

    const showUnsavedModal = !isEqual(this.defaultValues, this.form.getRawValue());

    if (showUnsavedModal) {
      const resp = await this.showUnsavedChanges();

      if (resp) {
        this.ref.close();
      }

      return;
    }

    this.ref.close();
  };

  constructor() {
    this.setValidators();

    this.siteCurveService
      .get()
      .pipe(takeUntilDestroyed())
      .subscribe(({ data }) => {
        if (data) {
          this.allSiteCurves.set(data);
        }
      });

    this.form
      .get('siteDriverPeriod')
      ?.valueChanges.pipe(takeUntilDestroyed())
      .subscribe((period) => {
        const siteCurves = this.allSiteCurves().filter(
          ({ curve_type }) => curve_type === (period || CurveType.NET)
        );

        this.siteCurves.set(siteCurves);
      });

    this.form
      .get('driver')
      ?.valueChanges.pipe(takeUntilDestroyed())
      .subscribe((driver) => {
        // trigger validation after driver change for date picker in case if it was invalid
        // to keep errors persistent
        if (driver === 'time') {
          setTimeout(() => {
            if (this.isAnyTimelineMilestoneTouched) {
              [
                'startMilestone',
                'endMilestone',
                'customStartMilestone',
                'customEndMilestone',
              ].forEach((field) => this.revalidateMilestoneField(field));
            }
          }, 0);
        }

        this.form.get('requiredTimeValidation')?.setErrors(null);
      });
  }

  disableMilestonesByCondition(
    value: null | string,
    controlName: string,
    dependingControlName?: string
  ) {
    let shouldDisable = !!value;

    const controlsToDisable = [
      'startMilestone',
      'endMilestone',
      'customStartMilestone',
      'customEndMilestone',
    ].filter((control) => control !== controlName && control !== dependingControlName);

    if (dependingControlName) {
      const dependingControl = this.form.get(dependingControlName);
      const control = this.form.get(controlName);

      // Check if either the depending control or the current control is touched and has a value
      shouldDisable =
        dependingControl?.touched || control?.touched
          ? shouldDisable || !!dependingControl?.value
          : shouldDisable;
    }

    controlsToDisable.forEach((control) => {
      if (shouldDisable) {
        this.form.get(control)?.disable();
      } else {
        this.form.get(control)?.enable();
      }
    });
  }

  getTimelinePhaseTooltip(item: MilestoneItem) {
    const datePipe = new DatePipe('en-US');

    const contractStartDate = datePipe.transform(item.contractStartDate, 'longDate');
    const contractEndDate = datePipe.transform(item.contractEndDate, 'longDate');

    return `${item.categoryName} ${contractStartDate} - ${contractEndDate}`;
  }

  getTimelineStartEndMilestoneTooltip(item: MilestoneItem, date: string) {
    const datePipe = new DatePipe('en-US');

    const formattedDate = datePipe.transform(date, 'longDate');

    return `${item.milestoneName} ${formattedDate}`;
  }

  get isAnyActivitySelected() {
    const { applyInvestigator, applyPassThrough, applyServices } = this.form.getRawValue();

    return applyServices || applyPassThrough || applyInvestigator;
  }

  get isAnyTimelineMilestoneHasValue() {
    const { customEndMilestone, customStartMilestone, endMilestone, milestone, startMilestone } =
      this.form.getRawValue();

    return !!(
      milestone ||
      startMilestone ||
      endMilestone ||
      customStartMilestone ||
      customEndMilestone
    );
  }

  get isAnyTimelineMilestoneTouched() {
    const { customEndMilestone, customStartMilestone, endMilestone, milestone, startMilestone } =
      this.form.controls;

    return (
      milestone.touched ||
      startMilestone.touched ||
      endMilestone.touched ||
      customStartMilestone.touched ||
      customEndMilestone.touched
    );
  }

  isCategoryExists(category: AmountType) {
    const vendorAmountTypes = this.mapVendorAmountTypes();
    const selectedVendors = this.selectedVendorIDs();

    return selectedVendors
      .map((vendorId) => vendorAmountTypes.get(vendorId))
      .flat()
      .includes(category);
  }

  isCategoryIncorrect(): boolean {
    const { applyInvestigator, applyPassThrough, applyServices } = this.form.controls;

    const isAnyActivityTouched =
      applyServices.touched || applyPassThrough.touched || applyInvestigator.touched;

    return (
      (isAnyActivityTouched || !!this.formGroup()?.submitted) &&
      !(applyServices.value || applyPassThrough.value || applyInvestigator.value)
    );
  }

  get isDriverIncorrect(): boolean {
    const driver = this.form.get('driver');

    return !!driver?.touched && !driver?.value;
  }

  private markAsTouchedDriverFieldBeforeSubmit() {
    if (this.isAnyActivitySelected) {
      this.form.get('driver')?.markAsTouched();
    }
  }

  revalidateCustomMilestoneField(datePicker?: InputComponent) {
    if (datePicker) {
      datePicker.revalidateDatepicker();
    }
  }

  revalidateMilestoneField(field: string) {
    this.form.get(field)?.updateValueAndValidity();
    this.form.get('timeMethod')?.updateValueAndValidity();
  }

  revalidateSiteCurveField() {
    this.form.get('siteCurve')?.updateValueAndValidity();
  }

  async saveForecastSettings() {
    this.markAsTouchedDriverFieldBeforeSubmit();

    const formValues = this.form.getRawValue();
    const { driver, milestone } = formValues;

    if (!driver) {
      return;
    }

    const validate = this.mapValidationForBeforeSubmit.get(driver);

    if (!validate) {
      console.error(`No validation function found for driver: ${driver}`);

      return;
    }

    if (!validate()) {
      markAllFormFieldsAsTouched(this.form, ['driver', 'requiredTimeValidation']);

      // Wrapping in timeout to handle inconsistent behavior with form error directive rendering
      // This ensures error messages are properly displayed after form validation
      setTimeout(() => {
        this.cdr.detectChanges();

        if (this.isDriverIncorrect || this.showTimeFormEmptyWarning) {
          document.querySelector('.section-error')?.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
        } else {
          scrollToFirstError();
        }
      }, 0);

      return;
    }

    try {
      this.submitting.set(true);

      const milestoneItem = this.milestonePhases().find(({ id }) => id === milestone);

      const milestoneCategory = driver === 'time' ? (milestoneItem?.id ?? null) : null;

      const isSuccess = await this.quickForecastService.saveUpdateBudgetForecastSettings(
        {
          ...formValues,
          milestone: milestoneItem || null,
          milestoneCategory,
          patientCurve: this.patientCurveQuery
            .patientCurves()
            .find(({ name }) => name === formValues.patientCurve)?.driver_setting_id,
          siteCurve: this.allSiteCurves().find(
            ({ curve_type, name }) =>
              name === formValues.siteCurve && curve_type === formValues.siteDriverPeriod
          )?.driver_setting_id,
        } as ForecastSettings,
        this.selectedVendorIDs()
      );

      if (isSuccess) {
        this.overlayService.success('Successfully saved!');
        this.ref.close();
      } else {
        this.overlayService.error('Failed to save settings');
      }
    } catch (error) {
      console.error('Error saving forecast settings:', error);
      this.overlayService.error('An error occurred while saving settings');
    } finally {
      this.submitting.set(false);
    }
  }

  private setValidators() {
    const {
      applyInvestigator,
      applyPassThrough,
      applyServices,
      customStartMilestone,
      siteCurve,
      siteDriverPeriod,
      startMilestone,
    } = this.form.controls;

    applyServices.addValidators(CustomValidators.oneOf(applyPassThrough, applyInvestigator));

    applyPassThrough.addValidators(CustomValidators.oneOf(applyServices, applyInvestigator));

    applyInvestigator.addValidators(CustomValidators.oneOf(applyServices, applyPassThrough));

    startMilestone.setValidators(QuickForecastValidators.startMilestone(this.form));

    siteCurve.addValidators(
      QuickForecastValidators.siteCurveBeforePeriod(siteDriverPeriod, this.formGroup)
    );

    customStartMilestone.addValidators(QuickForecastValidators.customStartMilestone(this.form));
  }

  get showTimeFormEmptyWarning() {
    const { driver, requiredTimeValidation } = this.form.controls;

    if (this.isDriverIncorrect || driver.value !== 'time') {
      return false;
    }

    const hasError = requiredTimeValidation?.errors !== null;

    if (
      !this.isAnyTimelineMilestoneHasValue &&
      this.isAnyTimelineMilestoneTouched &&
      this.formGroup()?.submitted &&
      hasError
    ) {
      return true;
    }

    return !this.isAnyTimelineMilestoneTouched && this.formGroup()?.submitted && hasError;
  }

  private async showUnsavedChanges() {
    const result = this.overlayService.openUnsavedChangesConfirmation();
    const event = await firstValueFrom(result.afterClosed$);

    return !!event.data;
  }
}
